import React, { useState, useMemo } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../../components/DirectionalIcon';
import { colors, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { WhatsAppCustomTemplate } from '../../services/api';
import { useThemedStyles } from '../../hooks/useThemedStyles';

// Same status/quality → color mapping TemplateManagementScreen.tsx already
// uses (STATUS_META/QUALITY_META), reused verbatim for visual consistency.
const STATUS_META: Record<string, { color: string; label: string }> = {
  APPROVED: { color: '#10B981', label: 'Approved' },
  PENDING: { color: '#F59E0B', label: 'Pending review' },
  REJECTED: { color: '#EF4444', label: 'Rejected' },
  PAUSED: { color: '#F59E0B', label: 'Paused' },
  DISABLED: { color: '#EF4444', label: 'Disabled' },
};

const QUALITY_META: Record<string, { color: string; label: string }> = {
  GREEN: { color: '#10B981', label: 'High quality' },
  YELLOW: { color: '#F59E0B', label: 'Medium quality' },
  RED: { color: '#EF4444', label: 'Low quality' },
  UNKNOWN: { color: colors.textLight as string, label: 'Quality pending' },
};

const PREVIEW_LIMIT = 5;

export interface TemplateMessagesCardProps {
  templates: WhatsAppCustomTemplate[];
  onViewAll?: () => void;
  onSettings?: () => void;
}

/** Template messages list — definitional data only (name/category/language/
 * status/quality). No usage/send-count column: that data doesn't exist
 * anywhere in the backend for templates, and inventing a number here would
 * be a correctness bug, not a design gap. Same list-card structure and
 * client-side search pattern as UsersTeamCard.tsx. Shows only a preview
 * slice — the full list lives in DashboardTemplatesAllScreen. */
export const TemplateMessagesCard: React.FC<TemplateMessagesCardProps> = ({ templates, onViewAll, onSettings }) => {
  const styles = useThemedStyles(getStyles);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? templates.filter((t) =>
          t.templateName.toLowerCase().includes(q) ||
          t.category.toLowerCase().includes(q) ||
          t.language.toLowerCase().includes(q)
        )
      : templates;
    return base.slice(0, PREVIEW_LIMIT);
  }, [templates, query]);

  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <Text style={styles.title}>Template messages</Text>
        <View style={styles.headerRight}>
          <Text style={styles.count}>{templates.length}</Text>
          {onSettings && (
            <TouchableOpacity onPress={onSettings} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="settings-outline" size={16} color={colors.text.secondary as string} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      <View style={styles.searchBox}>
        <Ionicons name="search-outline" size={14} color={colors.text.secondary as string} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search name, category, language"
          placeholderTextColor={colors.text.secondary as string}
          value={query}
          onChangeText={setQuery}
        />
      </View>

      {filtered.length === 0 ? (
        <View style={styles.empty}>
          <Ionicons name="document-text-outline" size={22} color={colors.text.secondary as string} />
          <Text style={styles.emptyText}>{templates.length === 0 ? 'No templates yet' : 'No matches'}</Text>
        </View>
      ) : (
        <View style={styles.list}>
          {filtered.map((t) => {
            const statusMeta = t.metaStatus ? STATUS_META[t.metaStatus] : undefined;
            const qualityMeta = t.metaQualityScore ? QUALITY_META[t.metaQualityScore] : undefined;
            return (
              <View key={t._id} style={styles.row}>
                <View style={styles.rowContent}>
                  <Text style={styles.name} numberOfLines={1}>{t.templateName}</Text>
                  <Text style={styles.meta} numberOfLines={1}>
                    {t.category} · {t.language}
                  </Text>
                </View>
                <View style={styles.badges}>
                  {statusMeta && (
                    <View style={[styles.badge, { backgroundColor: `${statusMeta.color}20` }]}>
                      <Text style={[styles.badgeText, { color: statusMeta.color }]}>{statusMeta.label}</Text>
                    </View>
                  )}
                  {qualityMeta && (
                    <View style={styles.statusLine}>
                      <View style={[styles.statusDot, { backgroundColor: qualityMeta.color }]} />
                      <Text style={styles.statusText}>{qualityMeta.label}</Text>
                    </View>
                  )}
                </View>
              </View>
            );
          })}
        </View>
      )}

      {onViewAll && templates.length > 0 && (
        <TouchableOpacity style={styles.viewAll} onPress={onViewAll} activeOpacity={0.7}>
          <Text style={styles.viewAllText}>View all</Text>
          <DirectionalIcon name="chevron-forward" size={14} color={colors.brand.primary as string} />
        </TouchableOpacity>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: designTokens.spacing.md,
    ...designTokens.shadows.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: designTokens.spacing.sm,
  },
  title: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
  },
  count: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.bold,
    color: colors.brand.primary,
    backgroundColor: colors.primaryTransparent,
    paddingHorizontal: designTokens.spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.full,
    overflow: 'hidden',
  },
  viewAll: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    marginTop: designTokens.spacing.sm,
    paddingTop: designTokens.spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  viewAllText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    color: colors.brand.primary,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.xs,
    backgroundColor: colors.background.base,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: designTokens.spacing.sm,
    marginBottom: designTokens.spacing.sm,
  },
  searchInput: {
    flex: 1,
    height: 32,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
  },
  list: {
    gap: designTokens.spacing.sm,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
  },
  rowContent: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
  },
  meta: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  badges: {
    alignItems: 'flex-end',
    gap: 4,
  },
  badge: {
    paddingHorizontal: designTokens.spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.full,
  },
  badgeText: {
    fontSize: 10,
    fontFamily: typography.fontFamily.semibold,
  },
  statusLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  statusText: {
    fontSize: 10,
    color: colors.text.secondary,
  },
  empty: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: designTokens.spacing.xs,
    paddingVertical: designTokens.spacing.lg,
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    textAlign: 'center',
  },
});
